import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { makeApi, type Attachment, type AuditEvent, type Comment, type Issue, type IssuePriority, type IssueStatus } from '../api/client';
import { STATUS_LABEL, PRIORITY_LABEL } from '../lib/format';
import { AttachmentItem } from '../components/AttachmentItem';
import { CommentForm } from '../components/CommentForm';
import { CommentItem } from '../components/CommentItem';
import { ConfirmModal } from '../components/ConfirmModal';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf'];
const MAX_BYTES = 10 * 1024 * 1024;
const COMMENTS_VISIBLE = 10;

type Tab = 'comments' | 'attachments' | 'activity';
type AllAttachment = Attachment & { source: 'issue' | { commentId: string; authorLabel: string } };

function auditEventLabel(ev: AuditEvent): string {
  switch (ev.eventType) {
    case 'issue_created': return 'Issue created';
    case 'status_changed': return `Status changed from ${STATUS_LABEL[ev.oldValue as keyof typeof STATUS_LABEL] ?? ev.oldValue} to ${STATUS_LABEL[ev.newValue as keyof typeof STATUS_LABEL] ?? ev.newValue}`;
    case 'priority_changed': return `Priority changed from ${PRIORITY_LABEL[ev.oldValue as keyof typeof PRIORITY_LABEL] ?? ev.oldValue} to ${PRIORITY_LABEL[ev.newValue as keyof typeof PRIORITY_LABEL] ?? ev.newValue}`;
    case 'title_changed': return 'Title updated';
    case 'description_changed': return 'Description updated';
    case 'comment_added': return 'Comment added';
    case 'attachment_added': return ev.newValue ? `Attachment added: ${ev.newValue}` : 'Attachment added';
    default: return ev.eventType;
  }
}

export function AdminIssueDetailPage(): JSX.Element {
  const { issueId = '' } = useParams<{ issueId: string }>();
  const [params] = useSearchParams();
  const projectId = params.get('projectId') ?? '';
  const addressId = params.get('addressId') ?? '';
  const { getToken, claims } = useAuth();
  const api = useMemo(() => makeApi(getToken), [getToken]);
  const mySub = claims?.sub ?? '';
  const issueFileRef = useRef<HTMLInputElement>(null);

  const [issue, setIssue] = useState<Issue | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [auditEvents, setAuditEvents] = useState<AuditEvent[]>([]);
  const [showAllComments, setShowAllComments] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('comments');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Issue edit state (title + description — status/priority are standalone selectors)
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');

  // Comment edit state
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);

  // Comment delete state
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(null);

  // Confirm modal state (issue-delete state removed 2026-04-28 — only comment delete remains)
  const [confirmCommentDeleteId, setConfirmCommentDeleteId] = useState<string | null>(null);

  // Memoized so object references are stable across keystrokes — AttachmentItem memo relies on this
  const allAttachments = useMemo<AllAttachment[]>(() => {
    if (!issue) return [];
    return [
      ...issue.attachments.map((a) => ({ ...a, source: 'issue' as const })),
      ...comments.flatMap((c) =>
        (c.attachments ?? []).map((a) => ({
          ...a,
          source: { commentId: c.commentId, authorLabel: c.authorDisplayName ?? c.authorEmail },
        })),
      ),
    ].sort((a, b) => b.uploadedAt.localeCompare(a.uploadedAt));
  }, [issue, comments]);

  const loadComments = useCallback(async () => {
    if (!issueId || !projectId || !addressId) return;
    try {
      const r = await api.adminListIssueComments(issueId, projectId, addressId);
      setComments(r.comments);
      setShowAllComments(false);
    } catch { /* non-fatal */ }
  }, [api, issueId, projectId, addressId]);

  const loadAuditEvents = useCallback(async () => {
    if (!issueId || !projectId || !addressId) return;
    try {
      const r = await api.adminListIssueAuditEvents(issueId, projectId, addressId);
      setAuditEvents(r.events);
    } catch { /* non-fatal */ }
  }, [api, issueId, projectId, addressId]);

  const load = useCallback(async () => {
    try {
      const r = await api.adminListIssues({ projectId, addressId });
      setIssue(r.issues.find((i) => i.issueId === issueId) ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    }
  }, [api, projectId, addressId, issueId]);

  useEffect(() => {
    load();
    loadComments();
    loadAuditEvents();
  }, [load, loadComments, loadAuditEvents]);

  // ---- status / priority update ----
  async function update(patch: { status?: IssueStatus; priority?: IssuePriority }): Promise<void> {
    if (!issue) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await api.adminPatchIssue(issue.issueId, { projectId: issue.projectId, addressId: issue.addressId, ...patch });
      setIssue((prev) =>
        prev ? { ...prev, status: updated.status, priority: updated.priority, updatedAt: updated.updatedAt } : prev,
      );
      loadAuditEvents();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update');
    } finally {
      setBusy(false);
    }
  }

  // ---- issue edit (title + description) ----
  function startEdit() {
    if (!issue) return;
    setEditTitle(issue.title);
    setEditDescription(issue.description);
    setEditing(true);
  }

  async function saveEdit(): Promise<void> {
    if (!issue) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await api.adminPatchIssue(issue.issueId, {
        projectId: issue.projectId,
        addressId: issue.addressId,
        title: editTitle,
        description: editDescription,
      });
      setIssue((prev) =>
        prev ? { ...prev, title: updated.title, description: updated.description, updatedAt: updated.updatedAt } : prev,
      );
      setEditing(false);
      loadAuditEvents();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setBusy(false);
    }
  }

  // ---- issue-level attachment upload ----
  async function onIssueFile(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !issue) return;
    if (!ALLOWED_TYPES.includes(file.type)) { setError('Only JPEG/PNG/WebP/GIF/PDF allowed'); return; }
    if (file.size > MAX_BYTES) { setError('File too large (max 10 MB)'); return; }
    setBusy(true);
    setError(null);
    try {
      const { uploadUrl } = await api.adminRequestAttachmentUpload(issue.issueId, {
        projectId: issue.projectId, addressId: issue.addressId,
        filename: file.name, contentType: file.type, size: file.size,
      });
      const put = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
      if (!put.ok) throw new Error(`Upload failed: ${put.status}`);
      load();
      loadAuditEvents();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setBusy(false);
    }
  }

  // ---- comment submission (called by CommentForm) ----
  const handleCommentSubmit = useCallback(async (body: string, files: File[]): Promise<void> => {
    if (!issue) return;
    setError(null);
    try {
      const newComment = await api.adminCreateIssueComment(issue.issueId, issue.projectId, issue.addressId, body);
      for (const file of files) {
        try {
          const { uploadUrl } = await api.adminAddCommentAttachment(
            issue.issueId, newComment.commentId, issue.projectId, issue.addressId,
            { filename: file.name, contentType: file.type, size: file.size },
          );
          await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
        } catch {
          setError(`Comment posted but failed to upload ${file.name}`);
        }
      }
      loadComments();
      loadAuditEvents();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to post comment');
      throw e;
    }
  }, [api, issue, loadComments, loadAuditEvents]);

  // ---- comment edit ----
  const handleCommentSave = useCallback(async (commentId: string, body: string): Promise<void> => {
    if (!issue) return;
    try {
      const updated = await api.adminUpdateIssueComment(issue.issueId, commentId, issue.projectId, issue.addressId, body);
      setComments((prev) => prev.map((c) => (c.commentId === updated.commentId ? updated : c)));
      setEditingCommentId(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update comment');
      throw e;
    }
  }, [api, issue]);

  async function deleteComment(commentId: string): Promise<void> {
    setConfirmCommentDeleteId(commentId);
  }

  async function doDeleteComment(commentId: string): Promise<void> {
    if (!issue) return;
    setConfirmCommentDeleteId(null);
    setDeletingCommentId(commentId);
    setError(null);
    try {
      await api.adminDeleteIssueComment(issue.issueId, commentId, issue.projectId, issue.addressId);
      setComments((prev) => prev.filter((c) => c.commentId !== commentId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete comment');
    } finally {
      setDeletingCommentId(null);
    }
  }

  if (!issue && !error) return <div className="text-gray-500">Loading…</div>;
  if (!issue) return <div className="text-red-400">{error ?? 'Issue not found'}</div>;

  const totalAttachments = allAttachments.length;

  function issueAttachmentDownloadUrl(attachmentId: string): Promise<string> {
    return api.adminGetAttachmentDownload(issue!.issueId, attachmentId, issue!.projectId, issue!.addressId)
      .then((r) => r.downloadUrl);
  }

  function commentAttachmentDownloadUrl(commentId: string) {
    return (attachmentId: string): Promise<string> =>
      api.adminGetCommentAttachmentDownload(issue!.issueId, commentId, attachmentId, issue!.projectId, issue!.addressId)
        .then((r) => r.downloadUrl);
  }

  function aggregatedDownloadUrl(
    src: 'issue' | { commentId: string; authorLabel: string },
  ) {
    return (attachmentId: string): Promise<string> =>
      src === 'issue'
        ? issueAttachmentDownloadUrl(attachmentId)
        : commentAttachmentDownloadUrl(src.commentId)(attachmentId);
  }

  return (
    <div className="space-y-6">
      <div>
        <Link to="/admin/issues" className="text-sm text-red-400 hover:text-red-300 hover:underline">&larr; Back</Link>
      </div>

      {/* Issue details card */}
      <div className="card space-y-3">
        {issue.issueReferenceNumber && (
          <div className="text-xs font-mono text-gray-500">{issue.issueReferenceNumber}</div>
        )}
        {editing ? (
          <input
            className="input w-full text-xl font-semibold"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            maxLength={200}
          />
        ) : (
          <h1 className="text-xl font-semibold text-white">{issue.title}</h1>
        )}
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 text-sm items-center">
          <dt className="text-gray-500 font-medium">Project</dt>
          <dd className="text-gray-200">
            {issue.projectDisplayName && issue.projectReferenceNumber
              ? `${issue.projectDisplayName} (${issue.projectReferenceNumber})`
              : issue.projectDisplayName || issue.projectReferenceNumber || issue.projectId}
          </dd>
          <dt className="text-gray-500 font-medium">Address</dt>
          <dd className="text-gray-200">{issue.ownerAddress ?? issue.addressId}</dd>
          <dt className="text-gray-500 font-medium">Reported by</dt>
          <dd className="text-gray-200">{issue.ownerEmail ?? issue.ownerSub}</dd>
          <dt className="text-gray-500 font-medium">Status</dt>
          <dd>
            <select
              className="input py-1 w-auto"
              value={issue.status}
              disabled={busy || editing}
              onChange={(e) => update({ status: e.target.value as IssueStatus })}
            >
              {(['open', 'in_progress', 'resolved'] as IssueStatus[]).map((s) => (
                <option key={s} value={s}>{STATUS_LABEL[s]}</option>
              ))}
            </select>
          </dd>
          <dt className="text-gray-500 font-medium">Priority</dt>
          <dd>
            <select
              className="input py-1 w-auto"
              value={issue.priority}
              disabled={busy || editing}
              onChange={(e) => update({ priority: e.target.value as IssuePriority })}
            >
              {(['low', 'medium', 'high'] as IssuePriority[]).map((p) => (
                <option key={p} value={p}>{PRIORITY_LABEL[p]}</option>
              ))}
            </select>
          </dd>
          <dt className="text-gray-500 font-medium">Created</dt>
          <dd className="text-gray-500 text-xs">{new Date(issue.createdAt).toLocaleString()}</dd>
          <dt className="text-gray-500 font-medium">Last updated</dt>
          <dd className="text-gray-500 text-xs">{new Date(issue.updatedAt).toLocaleString()}</dd>
        </dl>
        <div className="border-t border-gray-800 pt-3">
          {editing ? (
            <textarea
              className="input w-full min-h-[140px]"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              maxLength={10000}
            />
          ) : (
            <p className="whitespace-pre-wrap text-gray-300">
              {issue.description || <span className="text-gray-600 italic">(no description)</span>}
            </p>
          )}
        </div>
        {issue.attachments.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {issue.attachments.map((a) => (
              <AttachmentItem
                key={a.attachmentId}
                attachment={a}
                getDownloadUrl={issueAttachmentDownloadUrl}
              />
            ))}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-gray-800">
          <div className="flex gap-2">
            {editing ? (
              <>
                <button className="btn-primary" disabled={busy || !editTitle.trim()} onClick={saveEdit}>
                  {busy ? 'Saving…' : 'Save'}
                </button>
                <button className="btn-secondary" disabled={busy} onClick={() => setEditing(false)}>
                  Cancel
                </button>
              </>
            ) : (
              <button className="btn-secondary" disabled={busy} onClick={startEdit}>
                Edit title &amp; description
              </button>
            )}
          </div>
          {/* Issue deletion removed 2026-04-28 — issues are immutable once
             created; the only "remove from active list" is status: resolved.
             See portal-api/src/handlers/api.ts where adminDeleteIssue was
             also stripped. */}
        </div>
      </div>

      {/* Tab bar */}
      <div className="card p-0 overflow-hidden">
        <div className="flex border-b border-gray-800">
          {(['comments', 'attachments', 'activity'] as Tab[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-red-500 text-red-400'
                  : 'border-transparent text-gray-500 hover:text-gray-200'
              }`}
            >
              {tab === 'comments'
                ? `Comments (${comments.length})`
                : tab === 'attachments'
                ? `Attachments (${totalAttachments})`
                : `Activity (${auditEvents.length})`}
            </button>
          ))}
        </div>

        {/* Comments tab */}
        {activeTab === 'comments' && (
          <div className="p-4 space-y-3">
            {comments.length === 0 ? (
              <p className="text-sm text-gray-500">No comments yet.</p>
            ) : (
              <ul className="space-y-3">
                {comments.length > COMMENTS_VISIBLE && !showAllComments && (
                  <li>
                    <button className="text-xs text-red-400 hover:underline" onClick={() => setShowAllComments(true)}>
                      Show {comments.length - COMMENTS_VISIBLE} older comment{comments.length - COMMENTS_VISIBLE !== 1 ? 's' : ''}
                    </button>
                  </li>
                )}
                {(showAllComments ? comments : comments.slice(-COMMENTS_VISIBLE)).map((c) => (
                  <CommentItem
                    key={c.commentId}
                    comment={c}
                    isEditing={editingCommentId === c.commentId}
                    isDeleting={deletingCommentId === c.commentId}
                    isMine={c.authorSub === mySub}
                    onStartEdit={() => setEditingCommentId(c.commentId)}
                    onCancelEdit={() => setEditingCommentId(null)}
                    onSave={handleCommentSave}
                    onDelete={deleteComment}
                    getDownloadUrl={commentAttachmentDownloadUrl(c.commentId)}
                  />
                ))}
              </ul>
            )}

            {/* New comment form — owns its own state so keystrokes don't re-render the page */}
            <CommentForm onSubmit={handleCommentSubmit} onError={setError} />
          </div>
        )}

        {/* Attachments tab */}
        {activeTab === 'attachments' && (
          <div className="p-4 space-y-3">
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-sm text-gray-500 flex-1">
                {totalAttachments === 0 ? 'No attachments yet.' : `${totalAttachments} attachment${totalAttachments !== 1 ? 's' : ''}`}
              </span>
              <button className="btn-secondary text-sm" disabled={busy} onClick={() => issueFileRef.current?.click()}>
                {busy ? 'Uploading…' : 'Add file to issue'}
              </button>
              <input ref={issueFileRef} type="file" className="hidden" accept={ALLOWED_TYPES.join(',')} onChange={onIssueFile} />
            </div>

            {allAttachments.length === 0 ? null : (
              <ul className="divide-y divide-gray-800">
                {allAttachments.map((a) => (
                  <li key={a.attachmentId} className="py-2.5 flex items-center gap-3">
                    <AttachmentItem
                      attachment={a}
                      getDownloadUrl={aggregatedDownloadUrl(a.source)}
                      compact
                    />
                    <span className="text-xs text-gray-500 shrink-0 ml-auto">
                      {a.source === 'issue'
                        ? 'Issue'
                        : `Comment · ${a.source.authorLabel}`}
                      {' · '}{new Date(a.uploadedAt).toLocaleDateString()}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Activity tab */}
        {activeTab === 'activity' && (
          <div className="p-4">
            {auditEvents.length === 0 ? (
              <p className="text-sm text-gray-500">No activity yet.</p>
            ) : (
              <ol className="relative border-l border-gray-700 space-y-4 ml-2">
                {auditEvents.map((ev) => (
                  <li key={ev.eventId} className="ml-4">
                    <div className="absolute w-2.5 h-2.5 bg-gray-600 rounded-full -left-1.5 border border-gray-800 mt-1" />
                    <p className="text-sm text-gray-200">{auditEventLabel(ev)}</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {ev.actorEmail} · {new Date(ev.timestamp).toLocaleString()}
                    </p>
                  </li>
                ))}
              </ol>
            )}
          </div>
        )}
      </div>

      {error && <div className="text-sm text-red-400">{error}</div>}

      {confirmCommentDeleteId && (
        <ConfirmModal
          message="Delete this comment? This cannot be undone."
          confirmLabel="Delete"
          danger
          onConfirm={() => doDeleteComment(confirmCommentDeleteId)}
          onCancel={() => setConfirmCommentDeleteId(null)}
        />
      )}
    </div>
  );
}
